import React, { useEffect, useRef } from 'react';

interface AIOrbProps {
  size?: number;
  useWhiteVersion?: boolean;
  showText?: boolean;
  text?: string;
}

/**
 * A sleek, animated AI orb with liquid-like morphing effects.
 * Ported from Kotlin Compose to React/TypeScript.
 */
export const AIOrb: React.FC<AIOrbProps> = ({
  size = 48,
  useWhiteVersion = false,
  showText = true,
  text = 'AI'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    // Canvas needs to be large enough for bloom (bloom extends 2x beyond base radius)
    // But we keep the coordinate system simple
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const centerX = size / 2;
    const centerY = size / 2;
    // Base radius: for small icons, make it proportionally larger
    // Bloom extends 2x, so we need baseRadius * 2 < size/2, thus baseRadius < size/4
    // Using size/3.5 gives a nice balance for small icons
    const baseRadius = size / 3.5; // For 20px: ~5.7px base, ~11.4px with bloom

    // Animation function
    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;

      // Calculate animation values
      const rotation = (elapsed / 6000) * 360 % 360;

      // Liquid morphing distortions (reverse animations)
      const morph1 = Math.sin((elapsed / 1800) * Math.PI * 2);
      const morph2 = Math.sin((elapsed / 2100) * Math.PI * 2);
      const morph3 = Math.sin((elapsed / 2500) * Math.PI * 2);
      const morph4 = Math.sin((elapsed / 2350) * Math.PI * 2);

      // Pulsating bloom (0.9 to 1.1)
      const bloomPulse = 1.0 + 0.1 * Math.sin((elapsed / 2000) * Math.PI * 2);

      // Clear canvas
      ctx.clearRect(0, 0, size, size);

      // Draw orb
      if (useWhiteVersion) {
        drawWhiteOrb(ctx, centerX, centerY, baseRadius, morph1, morph2, morph3, morph4, rotation, bloomPulse);
      } else {
        drawColorfulOrb(ctx, centerX, centerY, baseRadius, morph1, morph2, morph3, morph4, rotation, bloomPulse);
      }

      // Draw text
      if (showText) {
        const textColor = useWhiteVersion ? '#2196F3' : '#FFFFFF';
        drawText(ctx, centerX, centerY, baseRadius, textColor, text);
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [size, useWhiteVersion, showText, text]);

  return <canvas ref={canvasRef} className="block" />;
};

/**
 * Creates a liquid blob path using control points around a circle with smooth, organic curves
 */
function createLiquidBlobPath(
  centerX: number,
  centerY: number,
  baseRadius: number,
  morphOffsets: number[]
): Path2D {
  const path = new Path2D();
  const numPoints = 12;
  const angleStep = 360 / numPoints;

  // Generate points around the circle with morphing distortions
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < numPoints; i++) {
    const angle = (angleStep * i) * Math.PI / 180;

    // Use multiple morph values for more randomness
    const morphIndex1 = i % morphOffsets.length;
    const morphIndex2 = (i + 1) % morphOffsets.length;
    const blendedMorph = (morphOffsets[morphIndex1] + morphOffsets[morphIndex2] * 0.5) / 1.5;

    // Add some variation per-point with different distortion ranges
    const variation = i % 3 === 0 ? 0.2 : i % 2 === 0 ? 0.15 : 0.18;
    const distortion = 1 + (blendedMorph * variation);
    const radius = baseRadius * distortion;

    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);
    points.push({ x, y });
  }

  // Create smooth organic curve using cubic bezier curves
  path.moveTo(points[0].x, points[0].y);

  for (let i = 0; i < numPoints; i++) {
    const current = points[i];
    const next = points[(i + 1) % numPoints];
    const nextNext = points[(i + 2) % numPoints];

    const tangentLength = 0.4;

    // Control point 1: offset from current point toward next
    const control1X = current.x + (next.x - current.x) * tangentLength;
    const control1Y = current.y + (next.y - current.y) * tangentLength;

    // Control point 2: offset from next point toward current
    const control2X = next.x - (nextNext.x - current.x) * tangentLength * 0.5;
    const control2Y = next.y - (nextNext.y - current.y) * tangentLength * 0.5;

    path.bezierCurveTo(
      control1X, control1Y,
      control2X, control2Y,
      next.x, next.y
    );
  }

  path.closePath();
  return path;
}

function drawWhiteOrb(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  baseRadius: number,
  morph1: number,
  morph2: number,
  morph3: number,
  morph4: number,
  rotation: number,
  bloomPulse: number
) {
  const mainBlobPath = createLiquidBlobPath(centerX, centerY, baseRadius, [morph1, morph2, morph3, morph4]);

  // Multi-layer bloom effect - outermost layer
  const gradient1 = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 2.0 * bloomPulse);
  gradient1.addColorStop(0, 'rgba(255, 255, 255, 0.25)');
  gradient1.addColorStop(0.33, 'rgba(255, 255, 255, 0.15)');
  gradient1.addColorStop(0.66, 'rgba(255, 255, 255, 0.08)');
  gradient1.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = gradient1;
  ctx.beginPath();
  ctx.arc(centerX, centerY, baseRadius * 2.0 * bloomPulse, 0, Math.PI * 2);
  ctx.fill();

  // Outer bloom layer
  const gradient2 = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 1.6 * bloomPulse);
  gradient2.addColorStop(0, 'rgba(255, 255, 255, 0.35)');
  gradient2.addColorStop(0.33, 'rgba(255, 255, 255, 0.22)');
  gradient2.addColorStop(0.66, 'rgba(255, 255, 255, 0.12)');
  gradient2.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = gradient2;
  ctx.beginPath();
  ctx.arc(centerX, centerY, baseRadius * 1.6 * bloomPulse, 0, Math.PI * 2);
  ctx.fill();

  // Medium bloom layer
  const gradient3 = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 1.3 * bloomPulse);
  gradient3.addColorStop(0, 'rgba(255, 255, 255, 0.5)');
  gradient3.addColorStop(0.33, 'rgba(255, 255, 255, 0.3)');
  gradient3.addColorStop(0.66, 'rgba(255, 255, 255, 0.15)');
  gradient3.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = gradient3;
  ctx.beginPath();
  ctx.arc(centerX, centerY, baseRadius * 1.3 * bloomPulse, 0, Math.PI * 2);
  ctx.fill();

  // Inner bright glow
  const gradient4 = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 1.15 * bloomPulse);
  gradient4.addColorStop(0, 'rgba(255, 255, 255, 0.7)');
  gradient4.addColorStop(0.33, 'rgba(255, 255, 255, 0.45)');
  gradient4.addColorStop(0.66, 'rgba(255, 255, 255, 0.25)');
  gradient4.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = gradient4;
  ctx.beginPath();
  ctx.arc(centerX, centerY, baseRadius * 1.15 * bloomPulse, 0, Math.PI * 2);
  ctx.fill();

  // Main blob
  const blobGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius);
  blobGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
  blobGradient.addColorStop(1, 'rgba(255, 255, 255, 1)');
  ctx.fillStyle = blobGradient;
  ctx.fill(mainBlobPath);
}

function drawColorfulOrb(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  baseRadius: number,
  morph1: number,
  morph2: number,
  morph3: number,
  morph4: number,
  rotation: number,
  bloomPulse: number
) {
  const bluePrimary = '#2196F3';
  const mainBlobPath = createLiquidBlobPath(centerX, centerY, baseRadius, [morph1, morph2, morph3, morph4]);

  // Multi-layer bloom effect - outermost layer
  const gradient1 = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 2.0 * bloomPulse);
  gradient1.addColorStop(0, 'rgba(33, 150, 243, 0.3)');
  gradient1.addColorStop(0.33, 'rgba(33, 150, 243, 0.18)');
  gradient1.addColorStop(0.66, 'rgba(33, 150, 243, 0.1)');
  gradient1.addColorStop(1, 'rgba(33, 150, 243, 0)');
  ctx.fillStyle = gradient1;
  ctx.beginPath();
  ctx.arc(centerX, centerY, baseRadius * 2.0 * bloomPulse, 0, Math.PI * 2);
  ctx.fill();

  // Outer bloom layer
  const gradient2 = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 1.6 * bloomPulse);
  gradient2.addColorStop(0, 'rgba(33, 150, 243, 0.45)');
  gradient2.addColorStop(0.33, 'rgba(33, 150, 243, 0.28)');
  gradient2.addColorStop(0.66, 'rgba(33, 150, 243, 0.15)');
  gradient2.addColorStop(1, 'rgba(33, 150, 243, 0)');
  ctx.fillStyle = gradient2;
  ctx.beginPath();
  ctx.arc(centerX, centerY, baseRadius * 1.6 * bloomPulse, 0, Math.PI * 2);
  ctx.fill();

  // Medium bloom layer
  const gradient3 = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 1.3 * bloomPulse);
  gradient3.addColorStop(0, 'rgba(33, 150, 243, 0.6)');
  gradient3.addColorStop(0.33, 'rgba(33, 150, 243, 0.4)');
  gradient3.addColorStop(0.66, 'rgba(33, 150, 243, 0.2)');
  gradient3.addColorStop(1, 'rgba(33, 150, 243, 0)');
  ctx.fillStyle = gradient3;
  ctx.beginPath();
  ctx.arc(centerX, centerY, baseRadius * 1.3 * bloomPulse, 0, Math.PI * 2);
  ctx.fill();

  // Inner bright glow
  const gradient4 = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius * 1.15 * bloomPulse);
  gradient4.addColorStop(0, 'rgba(33, 150, 243, 0.75)');
  gradient4.addColorStop(0.33, 'rgba(33, 150, 243, 0.5)');
  gradient4.addColorStop(0.66, 'rgba(33, 150, 243, 0.3)');
  gradient4.addColorStop(1, 'rgba(33, 150, 243, 0)');
  ctx.fillStyle = gradient4;
  ctx.beginPath();
  ctx.arc(centerX, centerY, baseRadius * 1.15 * bloomPulse, 0, Math.PI * 2);
  ctx.fill();

  // Main blob
  const blobGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius);
  blobGradient.addColorStop(0, 'rgba(33, 150, 243, 0.9)');
  blobGradient.addColorStop(1, bluePrimary);
  ctx.fillStyle = blobGradient;
  ctx.fill(mainBlobPath);
}

function drawText(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  baseRadius: number,
  textColor: string,
  text: string
) {
  // Calculate font size - increased to 55% of diameter for better readability
  const fontSize = baseRadius * 2 * 0.55;
  ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
  ctx.fillStyle = textColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, centerX, centerY);
}
